import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import {
  BadgeCheck,
  Check,
  Maximize2,
  Minimize2,
  Volume2,
  VolumeX,
} from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Link, useLocation, useSearch } from "wouter";

import { api, ApiError, type Racecard } from "@/api";
import { setOrientationGate } from "@/app/nativeOrientation";
import {
  activateRacingSafeMode,
  clearRacingSafeMode,
  racingSafeModeActive,
  reportClientIncident,
} from "@/app/nativeRecovery";
import { useBetSlip } from "@/betslip/BetSlipProvider";
import { OverlaySelect } from "@/components/OverlaySelect";
import {
  EmptyState,
  ErrorState,
  money,
  Panel,
  StatusBadge,
} from "@/components/ui";
import HomePage from "@/features/home/HomePage";
import { HomeBettingCard } from "@/features/home/HomeBettingCard";
import { CAMERA_OPTIONS, RaceTheater } from "@/features/racing/v2/RaceTheater";
import {
  useRaceCoupons,
  useTicketPlacement,
  useV2Tote,
} from "@/features/racing/v2/useV2Betting";
import {
  advanceCopy,
  type PendingAdvance,
  pickNextRace,
  RESULT_HOLD_MS,
} from "@/features/racing/raceLoop";
import { useRotationAutoFullscreen } from "@/features/racing/rotationFullscreen";
import { homeHref, homeSelection } from "@/features/racing/routes";
import { trackTitle } from "@/features/racing/venue";
import { useRaceClock } from "@/hooks/useRaceClock";
import {
  type CameraMode,
  RaceRenderer,
  type RacePlayback,
} from "@/renderer/RaceRenderer";
import { useRealtimeTopic } from "@/realtime/RealtimeProvider";
import { PlayerLoadingScreen } from "@/renderer/PlayerLoadingScreen";
import { usePlayerPresentation } from "@/renderer/usePlayerPresentation";
import { useSession } from "@/session/SessionProvider";

const EXOTIC_SIZE: Record<string, number> = {
  exacta: 2,
  quinella: 2,
  trifecta: 3,
};

export default function RacingPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const selection = homeSelection(search);
  const requestedRaceId = selection.watch;
  const [selectedRaceId, setSelectedRaceId] = useState<string | null>(
    requestedRaceId,
  );
  const [previousRequestedRaceId, setPreviousRequestedRaceId] =
    useState(requestedRaceId);
  const [liveWatchRaceId, setLiveWatchRaceId] = useState<string | null>(null);
  const [advanceAt, setAdvanceAt] = useState<number | null>(null);
  const betslip = useBetSlip();
  const { session } = useSession();
  const playerId = session?.playerId ?? null;
  // The product player is 3D-first. The verified 2D renderer is retained only
  // as an automatic WebGL failure/recovery fallback; it is not a user mode.
  const [safeMode, setSafeMode] = useState(racingSafeModeActive);
  const [viewerMode, setViewerMode] = useState<"3d" | "2d">(() =>
    racingSafeModeActive() ? "2d" : "3d",
  );
  const [cameraMode, setCameraMode] = useState<CameraMode>("auto");
  const [rideGate, setRideGate] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(
    () => window.localStorage.getItem("og:horseracing:sound") === "on",
  );
  const soundsRef = useRef<import("@renderer/sounds.js").RaceSounds | null>(
    null,
  );
  const handleRendererUnavailable = useCallback(() => {
    reportClientIncident("webgl_restore_failed");
    setViewerMode("2d");
    if (document.documentElement.dataset.nativePlatform === "ios") {
      activateRacingSafeMode();
      setSafeMode(true);
    }
  }, []);
  useRealtimeTopic("world.races");

  // The loop's transition request (auto intermission or explicit Continue).
  // The actual swap is gated below on the next racecard being local, so the
  // one persistent scene re-fields atomically — never through a loading
  // teardown.
  const [pendingAdvance, setPendingAdvance] = useState<PendingAdvance>(null);

  if (previousRequestedRaceId !== requestedRaceId) {
    setPreviousRequestedRaceId(requestedRaceId);
    setSelectedRaceId(requestedRaceId);
    setPendingAdvance(null);
    setAdvanceAt(null);
    setLiveWatchRaceId(null);
  }

  const selectRace = useCallback(
    (nextRaceId: string) => {
      setPendingAdvance(null);
      setAdvanceAt(null);
      setSelectedRaceId(nextRaceId);
      // Auto mode never writes history. An explicit live watch follows the
      // same loop by replacing only its watch intent, preserving the market.
      if (requestedRaceId) {
        navigate(homeHref({ watch: nextRaceId }, search), { replace: true });
      }
    },
    [navigate, requestedRaceId, search],
  );

  const upcoming = useQuery({
    queryKey: ["races", "upcoming", 20],
    queryFn: () => api.upcoming(20),
    refetchInterval: 10_000,
  });
  const firstUpcomingRaceId = pickNextRace(upcoming.data?.races ?? [], null);
  useEffect(() => {
    if (selectedRaceId || requestedRaceId || !firstUpcomingRaceId) return;
    const frame = window.requestAnimationFrame(() => {
      setSelectedRaceId((current) => current ?? firstUpcomingRaceId);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [firstUpcomingRaceId, requestedRaceId, selectedRaceId]);
  const raceId = requestedRaceId ?? selectedRaceId ?? firstUpcomingRaceId;
  useRealtimeTopic(raceId ? `race.${raceId}` : "world.races");

  const card = useQuery({
    queryKey: ["race", raceId, "card"],
    queryFn: () => api.racecard(raceId!),
    enabled: Boolean(raceId),
    // Never render the previous race's field against the next race's market
    // while navigation fetches. A stale cross-race pairing can make an open
    // market look locked and can submit the wrong race id.
    placeholderData: (previous) =>
      previous?.racecard.raceId === raceId ? previous : undefined,
  });
  const market = useQuery({
    queryKey: ["race", raceId, "market"],
    queryFn: () => api.markets(raceId!),
    enabled: Boolean(raceId),
    refetchInterval: 5_000,
  });
  const playback = useQuery({
    queryKey: ["race", raceId, "playback"],
    queryFn: async () => {
      try {
        return await api.playback(raceId!);
      } catch (error) {
        if (error instanceof ApiError && error.code === "PLAYBACK_PENDING") {
          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(raceId),
    refetchInterval: (query) =>
      query.state.data?.status === "official" ? false : 3_000,
  });

  const selectedCard = card.data?.racecard;
  // The one persistent viewer: the last READY card keeps the scene mounted
  // while a manually selected race's card still fetches (loop advances are
  // readiness-gated and never hit this window). A renderer teardown
  // mid-session is exactly the "page reload" this exists to avoid.
  const [viewerCard, setViewerCard] = useState<Racecard | null>(null);
  if (selectedCard && viewerCard !== selectedCard) setViewerCard(selectedCard);
  const displayCard = selectedCard ?? viewerCard;
  const currentSummary = upcoming.data?.races.find(
    (race) => race.raceId === raceId,
  );
  const status =
    playback.data?.status ??
    (market.data?.bettingOpen ? "betting" : market.data?.status) ??
    currentSummary?.status ??
    "scheduled";
  const raceClock = useRaceClock({
    gatesOpenAt:
      playback.data?.gatesOpenAt ??
      market.data?.scheduledAt ??
      selectedCard?.scheduledAt ??
      new Date().toISOString(),
    locksAt: market.data?.locksAt,
    serverTime: playback.data?.serverTime ?? market.data?.serverTime,
    status,
    playbackAvailable: Boolean(playback.data),
    raceDurationMs:
      playback.data?.raceDurationMs ?? selectedCard?.raceDurationMs ?? 40_000,
  });
  const bettingOpen = Boolean(
    market.data?.bettingOpen && raceClock.bettingCountdownMs > 0,
  );
  const effectiveMarket = useMemo(
    () =>
      market.data
        ? {
            ...market.data,
            bettingOpen,
          }
        : market.data,
    [bettingOpen, market.data],
  );

  // ---- Continuous broadcast: the tote serves the next OPEN betting window
  // even while the viewed race runs, so the next round's bets go on while
  // waiting. The viewer (renderer, clock, live HUD, result) stays on the
  // viewed race; the schedule's windows overlap by design.
  const nowMs = raceClock.nowMs;
  const bettableSummary = (upcoming.data?.races ?? []).find(
    (race) =>
      race.bettingOpensAt &&
      race.locksAt &&
      Date.parse(race.bettingOpensAt) <= nowMs &&
      nowMs < Date.parse(race.locksAt),
  );
  const bettingRaceId =
    selection.market ?? bettableSummary?.raceId ?? firstUpcomingRaceId;
  useRealtimeTopic(bettingRaceId ? `race.${bettingRaceId}` : "world.races");
  const bettingCardQuery = useQuery({
    queryKey: ["race", bettingRaceId, "card"],
    queryFn: () => api.racecard(bettingRaceId!),
    enabled: Boolean(bettingRaceId),
    placeholderData: (previous) =>
      previous?.racecard.raceId === bettingRaceId ? previous : undefined,
  });
  const bettingMarketQuery = useQuery({
    queryKey: ["race", bettingRaceId, "market"],
    queryFn: () => api.markets(bettingRaceId!),
    enabled: Boolean(bettingRaceId),
    refetchInterval: 5_000,
  });
  // Never substitute the displayed field while the next betting card loads.
  // Card, requested id and market must all agree before a wager can be added.
  const bettingCard =
    bettingCardQuery.data?.racecard.raceId === bettingRaceId
      ? bettingCardQuery.data.racecard
      : undefined;
  const bettingLocksAt = bettingMarketQuery.data?.locksAt ?? null;
  const bettingWindowMs = bettingLocksAt
    ? Math.max(0, Date.parse(bettingLocksAt) - nowMs)
    : 0;
  const bettingWindowOpen = Boolean(
    bettingCard &&
    bettingCard.raceId === bettingRaceId &&
    bettingMarketQuery.data?.raceId === bettingRaceId &&
    bettingMarketQuery.data.bettingOpen &&
    !bettingCardQuery.error &&
    !bettingMarketQuery.error &&
    bettingWindowMs > 0,
  );
  const effectiveBettingMarket = useMemo(
    () =>
      bettingMarketQuery.data?.raceId === bettingRaceId
        ? {
            ...bettingMarketQuery.data,
            bettingOpen: bettingWindowOpen,
          }
        : undefined,
    [bettingMarketQuery.data, bettingRaceId, bettingWindowOpen],
  );

  // ---- V2 betting state: one tote draft, one placement machine and one
  // coupons read model shared by the onsite panel and the fullscreen HUD.
  const { tote, getUnitStake, setUnitStake } = useV2Tote({
    card: bettingCard,
    market: effectiveBettingMarket,
  });
  const placement = useTicketPlacement({
    raceId: bettingCard?.raceId ?? null,
    playerId,
    tote,
    formatAmount: useCallback(
      (cents: number) => money(cents, session?.currency ?? "USD"),
      [session?.currency],
    ),
  });
  const coupons = useRaceCoupons({
    raceId: selectedCard?.raceId ?? null,
    playerId,
    settling: playback.data?.status === "official",
  });
  const toteSnapshot = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) =>
        tote.subscribe(() => onStoreChange(), { emitCurrent: false }),
      [tote],
    ),
    tote.getSnapshot,
    tote.getSnapshot,
  );

  // Drafting a new line after an acceptance starts the next ticket.
  useEffect(() => {
    if (
      placement.state.transaction === "accepted" &&
      toteSnapshot.lines.length > 0 &&
      effectiveBettingMarket?.bettingOpen
    ) {
      placement.resetToDraft();
    }
  }, [
    effectiveBettingMarket?.bettingOpen,
    placement,
    placement.state.transaction,
    toteSnapshot.lines.length,
  ]);

  // The fullscreen theater identifies the viewed race, unlike Home's
  // explicitly labelled next-race card. Never place its tote on another race.
  const theaterBettingOpen = Boolean(
    selectedCard &&
    bettingCard?.raceId === selectedCard.raceId &&
    effectiveMarket?.raceId === selectedCard.raceId &&
    effectiveMarket.bettingOpen &&
    effectiveBettingMarket?.raceId === selectedCard.raceId &&
    effectiveBettingMarket.bettingOpen,
  );
  const theaterMarket = useMemo(
    () =>
      effectiveMarket
        ? { ...effectiveMarket, bettingOpen: theaterBettingOpen }
        : effectiveMarket,
    [effectiveMarket, theaterBettingOpen],
  );
  const placeTicket = useCallback(() => {
    if (!theaterBettingOpen) return;
    void placement.place();
  }, [placement, theaterBettingOpen]);
  const checkTicketStatus = useCallback(() => {
    void placement.checkStatus();
  }, [placement]);
  const rebetTicket = useCallback(() => {
    placement.rebet();
  }, [placement]);

  function addHomeStraight(
    gate: number,
    betType: "win" | "place" | "show",
    launcher?: HTMLElement,
  ) {
    if (
      !bettingCard ||
      !effectiveBettingMarket?.bettingOpen ||
      effectiveBettingMarket.raceId !== bettingCard.raceId ||
      bettingCard.raceId !== bettingRaceId
    ) {
      return;
    }
    const runner = bettingCard.runners.find((entry) => entry.gate === gate);
    const oddsH = effectiveBettingMarket.board[betType]?.[gate];
    if (
      !runner ||
      typeof oddsH !== "number" ||
      !Number.isSafeInteger(oddsH) ||
      oddsH <= 0
    ) {
      return;
    }
    betslip.addLeg(
      {
        key: `${bettingCard.raceId}:${betType}:${gate}`,
        raceId: bettingCard.raceId,
        raceLabel: raceLabel(bettingCard),
        scheduledAt: bettingCard.scheduledAt,
        betType,
        sel: [gate],
        selectionLabel: `${runner.name} (#${gate + 1})`,
        oddsH,
      },
      launcher,
    );
  }

  function addExotic(
    betType: "exacta" | "quinella" | "trifecta",
    gates: number[],
    oddsH: number,
    launcher?: HTMLElement,
  ) {
    if (
      !bettingCard ||
      !effectiveBettingMarket?.bettingOpen ||
      effectiveBettingMarket.raceId !== bettingCard.raceId ||
      bettingCard.raceId !== bettingRaceId ||
      !Number.isSafeInteger(oddsH) ||
      oddsH <= 0 ||
      gates.length !== EXOTIC_SIZE[betType] ||
      new Set(gates).size !== gates.length ||
      !gates.every(
        (gate) =>
          Number.isSafeInteger(gate) &&
          gate >= 0 &&
          bettingCard.runners.some((runner) => runner.gate === gate),
      )
    ) {
      return;
    }
    // The panel binds its server quote to this market/type/selection before
    // invoking us. The global slip remains the sole quote/placement owner.
    const names = gates.map(
      (gate) =>
        bettingCard.runners.find((runner) => runner.gate === gate)!.name,
    );
    betslip.addLeg(
      {
        key: `${bettingCard.raceId}:${betType}:${gates.join("-")}`,
        raceId: bettingCard.raceId,
        raceLabel: raceLabel(bettingCard),
        scheduledAt: bettingCard.scheduledAt,
        betType,
        sel: [...gates],
        selectionLabel: names.join(betType === "quinella" ? " + " : " → "),
        oddsH,
      },
      launcher,
    );
  }

  // ---- Continuous broadcast: the loop's next round and its readiness.
  // The next racecard (and market) prefetch once the viewed race has gone
  // official (or the player asked to advance), so the swap is instant. NOT
  // while the race still runs: an enabled query both fetches mid-gallop and
  // joins every world.races/race.* invalidation refetch, and each of those
  // is main-thread JSON work competing with the 60fps scene. The result
  // hold (12s) leaves ample time to warm the swap.
  const historicalReplay = Boolean(
    playback.data?.status === "official" && liveWatchRaceId !== raceId,
  );
  // Only automatic Home selection skips an already-official schedule row.
  // An explicit watch/replay intent stays selected until Back to live.
  const resumeLive = !requestedRaceId && historicalReplay;
  const nextRaceId = resumeLive
    ? pickNextRace(
        (upcoming.data?.races ?? []).filter((race) => race.raceId !== raceId),
        null,
      )
    : pickNextRace(upcoming.data?.races ?? [], raceId ?? null);
  const prefetchNextRace =
    playback.data?.status === "official" || pendingAdvance !== null;
  const nextCard = useQuery({
    queryKey: ["race", nextRaceId, "card"],
    queryFn: () => api.racecard(nextRaceId!),
    enabled: Boolean(nextRaceId) && prefetchNextRace,
    placeholderData: (previous) =>
      previous?.racecard.raceId === nextRaceId ? previous : undefined,
  });
  useQuery({
    // Warm the next market alongside the card so the betting panel swaps
    // without a fetch flash (react-query dedupes with the live observers).
    queryKey: ["race", nextRaceId, "market"],
    queryFn: () => api.markets(nextRaceId!),
    enabled: Boolean(nextRaceId) && prefetchNextRace,
  });
  const nextCardReady = Boolean(
    nextRaceId && nextCard.data?.racecard.raceId === nextRaceId,
  );
  // The fullscreen intermission countdown counts to the next race's start.
  // scheduledAt comes from the public upcoming list — never information
  // a poller couldn't already see.
  const nextRaceStartsAt = nextRaceId
    ? ((upcoming.data?.races ?? []).find((race) => race.raceId === nextRaceId)
        ?.scheduledAt ??
      (nextCardReady ? (nextCard.data?.racecard.scheduledAt ?? null) : null))
    : null;

  // The explicit Continue action takes the same gated path as the
  // intermission scheduler (donor continueToNextCard → advanceToNextCard:
  // exactly one safe transition, duplicate requests are no-ops).
  const requestAdvance = useCallback(() => {
    setPendingAdvance((current) => current ?? "manual");
  }, []);

  // ONE canonical transition: the swap happens only when the next racecard
  // is already local, so viewer and theater move in the same render while
  // the independent market stays selected and the scene re-fields in place. If the schedule has
  // a gap (next race not authored/published yet), this waits — the upcoming
  // poll and the world.races topic re-run it when the card lands.
  useEffect(() => {
    if ((!pendingAdvance && !resumeLive) || !nextRaceId || !nextCardReady)
      return;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      selectRace(nextRaceId);
    });
    return () => {
      cancelled = true;
    };
  }, [nextCardReady, nextRaceId, pendingAdvance, resumeLive, selectRace]);

  // ---- The viewer follows the schedule. A race watched live holds its
  // official result briefly, then flows to the next round — whose betting
  // window is already open — without any click. A result opened AFTER it
  // settled (explicit watch/replay) never auto-advances.
  const playbackStatus = playback.data?.status ?? null;
  const playbackRaceId = playback.data?.raceId ?? null;

  // ---- The casino ending: a live-watched finish replays the podium in
  // slow motion before ANY result presentation appears; the official
  // terminal, the onsite result panel and the next-round hold all wait for
  // the renderer's replay-complete handoff. Official status alone can never
  // skip this sequence.
  const [finishSeq, setFinishSeq] = useState<{
    raceId: string | null;
    phase: "idle" | "replay" | "done";
  }>({ raceId: null, phase: "idle" });
  if (finishSeq.raceId !== null && finishSeq.raceId !== raceId) {
    setFinishSeq({ raceId: null, phase: "idle" });
  }
  const handleFinishSequence = useCallback(
    (phase: "replay" | "complete") => {
      setFinishSeq({
        raceId,
        phase: phase === "replay" ? "replay" : "done",
      });
    },
    [raceId],
  );
  const liveWatchedCurrent = liveWatchRaceId === raceId;
  const finishReplayActive =
    liveWatchedCurrent &&
    finishSeq.raceId === raceId &&
    finishSeq.phase === "replay";
  const resultDisplayReady =
    !liveWatchedCurrent ||
    (finishSeq.raceId === raceId && finishSeq.phase === "done");

  // A ticket holder never flows past "payout pending": the hold starts
  // once settlement is authoritative (or immediately without a ticket) AND
  // the finish replay has handed the screen to the result presentation.
  const settlementDisplayed = !coupons.hasTicket || coupons.allSettled;
  useEffect(() => {
    if (
      !raceId ||
      playbackRaceId !== raceId ||
      playbackStatus !== "official" ||
      !settlementDisplayed ||
      !resultDisplayReady
    ) {
      if (
        raceId &&
        playbackRaceId === raceId &&
        playbackStatus &&
        playbackStatus !== "official"
      ) {
        queueMicrotask(() => {
          setLiveWatchRaceId((current) =>
            current === raceId ? current : raceId,
          );
        });
      }
      queueMicrotask(() => {
        setAdvanceAt((current) => (current === null ? current : null));
      });
      return;
    }
    if (liveWatchRaceId !== raceId) return;
    queueMicrotask(() => {
      setAdvanceAt((current) => current ?? Date.now() + RESULT_HOLD_MS);
    });
  }, [
    liveWatchRaceId,
    playbackRaceId,
    playbackStatus,
    raceId,
    resultDisplayReady,
    settlementDisplayed,
  ]);

  useEffect(() => {
    if (advanceAt === null) return;
    const timer = window.setTimeout(
      () => {
        setAdvanceAt(null);
        setPendingAdvance((current) => current ?? "auto");
      },
      Math.max(0, advanceAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [advanceAt]);

  const nextRaceCountdownCopy = advanceCopy({
    advanceAt,
    nowMs: raceClock.nowMs,
    pending: pendingAdvance,
    nextRaceKnown: Boolean(nextRaceId),
    nextRaceReady: nextCardReady,
  });
  useEffect(() => {
    let active = true;
    void import("@renderer/sounds.js").then(({ RaceSounds }) => {
      if (!active) return;
      const sounds = new RaceSounds();
      sounds.enabled =
        window.localStorage.getItem("og:horseracing:sound") === "on";
      soundsRef.current = sounds;
      void sounds.prefetch();
    });
    return () => {
      active = false;
      soundsRef.current?.stopAll(0.1);
      soundsRef.current = null;
    };
  }, []);

  useEffect(() => {
    const sounds = soundsRef.current;
    if (!sounds || !soundEnabled) return;
    if (status === "running" && playback.data) {
      const elapsed = Math.max(
        0,
        (Date.now() - Date.parse(playback.data.startedAt)) / 1000,
      );
      sounds.race(40, elapsed);
    } else if (status === "official") {
      sounds.results(false);
    } else if (effectiveMarket?.bettingOpen) {
      sounds.betting();
    } else {
      sounds.gates();
    }
  }, [status, soundEnabled, playback.data, effectiveMarket?.bettingOpen]);

  function toggleSound() {
    const enabled = !soundEnabled;
    setSoundEnabled(enabled);
    soundsRef.current?.setEnabled(enabled);
  }

  const handleResultPrint = useCallback(
    (event: string, payload?: Record<string, unknown>) => {
      const sounds = soundsRef.current;
      if (!sounds) return;
      if (event === "start") {
        sounds.resultPrintStart(Number(payload?.rows ?? 8));
      } else if (event === "row") {
        sounds.resultPrintTick(Number(payload?.row ?? 0));
      } else if (event === "commit") {
        sounds.resultPrintCommit();
      } else if (event === "cancel") {
        sounds.resultPrintCancel();
      }
    },
    [],
  );
  const handleOfficialResult = useCallback(
    (netCents: number, printed: boolean) => {
      // The celebratory sting is gated on the committed print AND an
      // authoritative positive net — same rule as the donor host.
      if (printed && netCents > 0) soundsRef.current?.results(true);
    },
    [],
  );

  return (
    <div
      className="page page--racing page--home-showcase"
      data-finish-sequence={
        finishSeq.raceId === raceId ? finishSeq.phase : "idle"
      }
      data-race-id={raceId}
    >
      {safeMode ? (
        <div className="racing-safe-mode" role="status">
          <span>Lightweight player active after an iOS recovery.</span>
          <button
            onClick={() => {
              clearRacingSafeMode();
              setSafeMode(false);
              setViewerMode("3d");
            }}
            type="button"
          >
            Retry 3D
          </button>
        </div>
      ) : null}
      <section className="home-broadcast">
        <div className="race-titlebar home-broadcast__header">
          <div>
            {displayCard ? <StatusBadge status={raceClock.phase} /> : null}
            <h1>
              {requestedRaceId && historicalReplay
                ? "Race replay"
                : "Live racing"}
            </h1>
            <span>
              {displayCard
                ? `${trackTitle(displayCard)} · ${displayCard.distanceM}m · ${displayCard.runners.length} runners`
                : upcoming.error || card.error
                  ? "Live broadcast unavailable"
                  : upcoming.isPending || raceId
                    ? "Loading the live broadcast…"
                    : "Waiting for published races"}
            </span>
          </div>
          {displayCard ? (
            <div className="race-titlebar__clock">
              <small>
                {raceClock.phase === "running"
                  ? "Race time"
                  : raceClock.phase === "betting"
                    ? "Closes in"
                    : raceClock.phase === "starting"
                      ? "Publishing playback"
                      : raceClock.phase === "official"
                        ? "Result"
                        : "Starts in"}
              </small>
              <strong>
                {raceClock.phase === "running"
                  ? `${(raceClock.elapsedMs / 1000).toFixed(1)}s`
                  : raceClock.phase === "starting"
                    ? "Starting…"
                    : raceClock.phase === "official"
                      ? "Official"
                      : raceClock.phase === "betting"
                        ? // "Closes in" counts to locksAt — the same number
                          // the fullscreen capsule shows (gates − 5s).
                          raceClock.bettingCountdownLabel
                        : raceClock.countdownLabel}
              </strong>
            </div>
          ) : null}
          {requestedRaceId ? (
            <button
              className="home-broadcast__link"
              type="button"
              onClick={() => {
                setPendingAdvance(null);
                setAdvanceAt(null);
                setLiveWatchRaceId(null);
                setPreviousRequestedRaceId(null);
                setSelectedRaceId(
                  pickNextRace(
                    (upcoming.data?.races ?? []).filter(
                      (race) =>
                        playback.data?.status !== "official" ||
                        race.raceId !== raceId,
                    ),
                    null,
                  ),
                );
                navigate(homeHref({ watch: null }, search));
              }}
            >
              Back to live
            </button>
          ) : null}
          <Link className="home-broadcast__link" href="/app/schedule">
            View schedule
          </Link>
        </div>

        {/* Watch, market and tab changes retain this one mounted Viewer. */}
        <div className="broadcast-layout">
          {displayCard ? (
            <Viewer
              cameraMode={cameraMode}
              card={displayCard}
              clock={raceClock}
              finishSequence={!historicalReplay && !safeMode}
              mode={viewerMode}
              onFinishSequence={handleFinishSequence}
              onRendererUnavailable={handleRendererUnavailable}
              // A playback stream may only ever drive the card it belongs to;
              // while a manually selected race's card still fetches the viewer
              // holds the previous field at the gates instead of tearing down.
              playback={selectedCard ? (playback.data ?? null) : null}
              replay={historicalReplay}
              rideGate={rideGate}
              setCameraMode={setCameraMode}
              setRideGate={setRideGate}
              soundEnabled={soundEnabled}
              theater={
                selectedCard
                  ? (active, theaterViewerEl) => (
                      <RaceTheater
                        active={active}
                        canRebet={placement.canRebet}
                        bettingCountdownLabel={raceClock.bettingCountdownLabel}
                        card={selectedCard}
                        clock={raceClock}
                        coupons={coupons}
                        getUnitStake={getUnitStake}
                        market={theaterMarket}
                        onCheckStatus={checkTicketStatus}
                        onContinue={requestAdvance}
                        onOfficialResult={handleOfficialResult}
                        onPlace={placeTicket}
                        onRebet={rebetTicket}
                        onResultPrint={handleResultPrint}
                        onToggleSound={toggleSound}
                        placement={placement.state}
                        playback={playback.data ?? null}
                        nextRaceStartsAt={nextRaceStartsAt}
                        racecardHash={card.data?.racecardHash ?? null}
                        resultCountdown={nextRaceCountdownCopy}
                        resultReady={resultDisplayReady}
                        finishReplayActive={finishReplayActive}
                        cameraMode={cameraMode}
                        rideGate={rideGate}
                        setCameraMode={setCameraMode}
                        setRideGate={setRideGate}
                        setUnitStake={setUnitStake}
                        soundEnabled={soundEnabled}
                        tote={tote}
                        viewer={theaterViewerEl}
                      />
                    )
                  : undefined
              }
              toggleSound={toggleSound}
            />
          ) : (
            <section
              aria-label="Live race broadcast"
              aria-busy={
                upcoming.isPending || Boolean(raceId && card.isPending)
              }
              className="panel race-viewer home-broadcast__placeholder"
            >
              <div className="race-viewer__toolbar" aria-hidden />
              <div className="race-renderer-stack">
                {upcoming.error ? (
                  <ErrorState
                    error={upcoming.error}
                    retry={() => void upcoming.refetch()}
                  />
                ) : card.error ? (
                  <ErrorState
                    error={card.error}
                    retry={() => void card.refetch()}
                  />
                ) : !raceId && !upcoming.isPending ? (
                  <EmptyState
                    title="No published races"
                    detail="The live broadcast will resume when the next race is published."
                  />
                ) : (
                  <PlayerLoadingScreen />
                )}
              </div>
            </section>
          )}
          <aside className="home-broadcast__betting">
            <HomeBettingCard
              card={bettingCard ?? null}
              market={effectiveBettingMarket}
              nowMs={raceClock.nowMs}
              watchingRaceId={displayCard?.raceId ?? null}
              tab={selection.tab}
              onTabChange={(tab) => navigate(homeHref({ tab }, search))}
              pinned={Boolean(selection.market)}
              onAuto={() => navigate(homeHref({ market: null }, search))}
              error={
                bettingCardQuery.error ??
                bettingMarketQuery.error ??
                (!bettingRaceId ? upcoming.error : null)
              }
              empty={!bettingRaceId && !upcoming.isPending && !upcoming.error}
              onRetry={() => {
                if (bettingRaceId) {
                  void bettingCardQuery.refetch();
                  void bettingMarketQuery.refetch();
                } else {
                  void upcoming.refetch();
                }
              }}
              onBet={addHomeStraight}
              onAddExotic={addExotic}
            />
          </aside>
        </div>
      </section>

      {displayCard && upcoming.error ? (
        <ErrorState
          error={upcoming.error}
          retry={() => void upcoming.refetch()}
        />
      ) : null}
      {displayCard && card.error ? (
        <ErrorState error={card.error} retry={() => void card.refetch()} />
      ) : null}
      {selectedCard &&
      playback.data?.raceId === selectedCard.raceId &&
      playback.data.status === "official" &&
      resultDisplayReady ? (
        <ResultPanel
          card={selectedCard}
          nextRaceCopy={nextRaceCountdownCopy}
          playback={playback.data}
        />
      ) : null}
      <HomePage
        currentRaceId={displayCard?.raceId ?? null}
        nowMs={Math.floor(raceClock.nowMs / 1000) * 1000}
      />
    </div>
  );
}

function Viewer({
  card,
  clock,
  playback,
  replay,
  finishSequence,
  onFinishSequence,
  onRendererUnavailable,
  mode,
  cameraMode,
  setCameraMode,
  rideGate,
  setRideGate,
  soundEnabled,
  toggleSound,
  theater,
}: {
  card: Racecard;
  clock: ReturnType<typeof useRaceClock>;
  playback: RacePlayback | null;
  replay: boolean;
  finishSequence: boolean;
  onFinishSequence: (phase: "replay" | "complete") => void;
  onRendererUnavailable: () => void;
  mode: "3d" | "2d";
  cameraMode: CameraMode;
  setCameraMode: (mode: CameraMode) => void;
  rideGate: number;
  setRideGate: (gate: number) => void;
  soundEnabled: boolean;
  toggleSound: () => void;
  theater?: (active: boolean, viewerEl: HTMLElement | null) => ReactNode;
}) {
  const viewerRef = useRef<HTMLElement>(null);
  const {
    ready: visualReady,
    loading: playerLoading,
    holding: frameHolding,
    holdCanvas: attachHoldCanvas,
    onReady: onVisualReady,
    attachViewer,
  } = usePlayerPresentation();
  const [loadProgress, setLoadProgress] = useState(0);
  const handleLoadProgress = useCallback((value: number) => {
    setLoadProgress(Math.max(0, Math.min(1, value / 100)));
  }, []);
  // The theater receives the viewer element as plain state (set from the
  // commit-phase callback ref) — refs are never read during render.
  const [viewerEl, setViewerEl] = useState<HTMLElement | null>(null);
  const bindViewer = useCallback(
    (element: HTMLElement | null) => {
      viewerRef.current = element;
      attachViewer(element);
      setViewerEl(element);
    },
    [attachViewer],
  );
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const [isFallbackFullscreen, setIsFallbackFullscreen] = useState(false);
  const isFullscreen = isNativeFullscreen || isFallbackFullscreen;

  useEffect(() => {
    const onFullscreenChange = () => {
      const activeElement =
        document.fullscreenElement ?? document.webkitFullscreenElement ?? null;
      const viewerIsFullscreen = activeElement === viewerRef.current;
      setIsNativeFullscreen(viewerIsFullscreen);
      if (viewerIsFullscreen) setIsFallbackFullscreen(false);
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        onFullscreenChange,
      );
    };
  }, []);

  // Mobile orientation contract (PLAN §"Racing V2 interface"): rotating to
  // landscape on the race page auto-enters fullscreen (fallback path — no
  // gesture, no permission); rotating back exits only rotation-entered
  // fullscreen. Manual fullscreen survives every rotation.
  useRotationAutoFullscreen({
    isFullscreen,
    onAutoEnter: useCallback(() => setIsFallbackFullscreen(true), []),
    onAutoExit: useCallback(() => setIsFallbackFullscreen(false), []),
  });

  // The native shells stay portrait-locked except while the race fullscreen
  // is active; browsers no-op. Cleanup covers exit AND unmount (navigating
  // away mid-fullscreen relocks portrait).
  useEffect(() => {
    if (!isFullscreen) return;
    setOrientationGate("landscape_allowed");
    return () => setOrientationGate("portrait");
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFallbackFullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsFallbackFullscreen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    };
  }, [isFallbackFullscreen]);

  const toggleFullscreen = async () => {
    const target = viewerRef.current;
    if (!target) return;
    if (isFallbackFullscreen) {
      setIsFallbackFullscreen(false);
      return;
    }
    const activeElement =
      document.fullscreenElement ?? document.webkitFullscreenElement ?? null;
    if (activeElement === target) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else await document.webkitExitFullscreen?.();
      return;
    }
    try {
      if (target.requestFullscreen) {
        await target.requestFullscreen();
        return;
      }
      if (target.webkitRequestFullscreen) {
        await target.webkitRequestFullscreen();
        return;
      }
    } catch {
      // Embedded browsers can expose the API while denying its permission.
    }
    setIsFallbackFullscreen(true);
  };

  return (
    <section
      className="panel race-viewer"
      data-player-ready={visualReady}
      data-player-loading={playerLoading}
      aria-busy={!visualReady}
      data-fullscreen={isFullscreen}
      data-fullscreen-fallback={isFallbackFullscreen}
      ref={bindViewer}
    >
      {mode === "3d" ? (
        <div
          className="race-viewer__toolbar"
          inert={!visualReady}
          aria-hidden={!visualReady}
        >
          <div aria-label="3D camera controls" role="group">
            <OverlaySelect
              ariaLabel="Camera mode"
              className="race-viewer__camera-select"
              onChange={setCameraMode}
              options={CAMERA_OPTIONS}
              value={cameraMode}
            />
            {(
              ["auto", "cinematic", "rail", "crane", "ride"] as CameraMode[]
            ).map((camera) => (
              <button
                className={clsx(
                  "race-viewer__camera-button",
                  cameraMode === camera && "is-active",
                )}
                key={camera}
                onClick={() => setCameraMode(camera)}
                type="button"
              >
                {camera}
              </button>
            ))}
            {cameraMode === "ride" ? (
              <OverlaySelect
                ariaLabel="Ride camera horse"
                className="race-viewer__ride-select"
                onChange={setRideGate}
                options={card.runners.map((runner) => ({
                  value: runner.gate,
                  label: `#${runner.gate + 1} ${runner.name}`,
                }))}
                value={rideGate}
              />
            ) : null}
            <button
              aria-label={
                soundEnabled ? "Mute race sound" : "Enable race sound"
              }
              onClick={toggleSound}
              type="button"
            >
              {soundEnabled ? (
                <Volume2 aria-hidden size={14} />
              ) : (
                <VolumeX aria-hidden size={14} />
              )}
            </button>
          </div>
        </div>
      ) : null}
      <RaceRenderer
        cameraMode={cameraMode}
        card={card}
        clockOffsetMs={clock.serverOffsetMs}
        finishSequence={finishSequence}
        fullscreen={isFullscreen}
        mode={mode}
        onFinishSequence={onFinishSequence}
        onUnavailable={onRendererUnavailable}
        onVisualReady={onVisualReady}
        onProgress={handleLoadProgress}
        playback={playback}
        replay={replay}
        rideGate={rideGate}
      />
      {!playback && !isFullscreen ? (
        <div className="race-viewer__countdown" aria-hidden={!visualReady}>
          <strong>
            {clock.phase === "starting" ? "Starting…" : clock.countdownLabel}
          </strong>
          <span>
            {clock.phase === "starting"
              ? "publishing verified playback"
              : "to the gates"}
          </span>
        </div>
      ) : null}
      {theater?.(isFullscreen && visualReady, viewerEl)}
      <canvas
        ref={attachHoldCanvas}
        className="player-frame-hold"
        data-visible={frameHolding}
        aria-hidden
      />
      <PlayerLoadingScreen
        ready={visualReady || !playerLoading}
        progress={loadProgress}
      />
      <button
        aria-label={
          isFullscreen ? "Exit race fullscreen" : "View race fullscreen"
        }
        className="race-viewer__fullscreen"
        disabled={!visualReady && !isFullscreen}
        tabIndex={visualReady || isFullscreen ? 0 : -1}
        aria-hidden={!visualReady && !isFullscreen}
        onClick={() => void toggleFullscreen()}
        title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
        type="button"
      >
        {isFullscreen ? (
          <Minimize2 aria-hidden size={18} />
        ) : (
          <Maximize2 aria-hidden size={18} />
        )}
      </button>
    </section>
  );
}

function ResultPanel({
  card,
  playback,
  nextRaceCopy,
}: {
  card: Racecard;
  playback: RacePlayback;
  nextRaceCopy?: string;
}) {
  return (
    <Panel className="race-result">
      <header>
        <BadgeCheck aria-hidden />
        <div>
          <strong>Official result</strong>
          <span>Verified against racecard {card.raceId.slice(0, 8)}…</span>
        </div>
        {nextRaceCopy ? (
          <span className="race-result__next">{nextRaceCopy}</span>
        ) : null}
      </header>
      <div>
        {playback.order.map((gate, index) => (
          <article key={gate}>
            <b>{index + 1}</b>
            <span className={`gate gate--${gate + 1}`}>{gate + 1}</span>
            <span>{card.runners[gate].name}</span>
            <strong>{(playback.finishTimesMs[gate] / 1000).toFixed(2)}s</strong>
            {index === 0 ? <Check aria-hidden size={14} /> : null}
          </article>
        ))}
      </div>
    </Panel>
  );
}

function raceLabel(card: Racecard) {
  const raceNumber =
    typeof card.chainIndex === "number"
      ? `R${card.chainIndex}`
      : typeof card.nonce === "number"
        ? `R${card.nonce}`
        : null;
  return [trackTitle(card), `${card.distanceM}m`, raceNumber]
    .filter(Boolean)
    .join(" · ");
}
